import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "../../../shared/api/base-query";
import { pagedQueryFn } from "../../../shared/api/paged-query-fn";
import type { QuerySubscriptionOptions } from "../../../shared/api/query-subscription-options";
import type { IAppUsersDto } from "../shared";
import type {
  ICreateAppUserDto,
  ISaveAppUserDto,
} from "../shared/dtos/save-app-user.dto";
import { AppUser } from "../shared/models/app-user";

const APP_USERS_LIST_TAG_TYPE = "AppUsersList";

function toAppUserModel(dto: IAppUsersDto): AppUser {
  const model = new AppUser(dto.id);
  model.updateFromDto(dto);
  return model;
}

export const appUsersApiSlice = createApi({
  reducerPath: "api-app-users",
  // https://github.com/reduxjs/redux-toolkit/discussions/2347#discussioncomment-2978038
  refetchOnFocus: false,
  keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
  baseQuery,
  tagTypes: [APP_USERS_LIST_TAG_TYPE],
  endpoints: (builder) => ({
    fetchAppUsers: builder.query<AppUser[], void>({
      queryFn: pagedQueryFn<IAppUsersDto, AppUser[]>({ url: "AppUsersV2" }, (dtos) => {
        const models: AppUser[] = dtos.map(toAppUserModel);
        models.sort((a: AppUser, b: AppUser) => a.lastName.localeCompare(b.lastName));
        return models;
      }),
      providesTags: [APP_USERS_LIST_TAG_TYPE],
    }),

    createAppUser: builder.mutation<AppUser, ICreateAppUserDto>({
      query: (body) => ({ url: "AppUsers", method: "POST", body }),
      transformResponse: toAppUserModel,
      invalidatesTags: [APP_USERS_LIST_TAG_TYPE],
    }),

    updateAppUser: builder.mutation<AppUser, { id: string; body: ISaveAppUserDto }>({
      query: ({ id, body }) => ({ url: `AppUsers/${id}`, method: "PATCH", body }),
      transformResponse: toAppUserModel,
      invalidatesTags: [APP_USERS_LIST_TAG_TYPE],
    }),
  }),
});

const useAppUsersEndpoint = appUsersApiSlice.endpoints.fetchAppUsers.useQuery;

export function useFetchAppUsers(
  options?: QuerySubscriptionOptions<typeof useAppUsersEndpoint>
) {
  return useAppUsersEndpoint(undefined, options);
}

export const { useCreateAppUserMutation, useUpdateAppUserMutation } = appUsersApiSlice;
